import { useState } from 'react';
import { cn } from '@rocco/utils/cn';

import type { LocationDataType } from '@modules/professional/type/contact-page-types';
import {
    MOBILE_INITIAL_LOCATION_NUM,
    hasMapCoordinates,
} from '@modules/professional/component/contact-tab/LocationConstant';
import { ProfessionalLocationCard } from './ProfessionalLocationCard';

export type ContactMobileView = 'list' | 'map';

const VIEWS: { value: ContactMobileView; label: string }[] = [
    { value: 'list', label: 'List' },
    { value: 'map', label: 'Map' },
];

interface ContactMobileViewToggleProps {
    view: ContactMobileView;
    onChange: (view: ContactMobileView) => void;
    locationCount: number;
}

/** List / Map switch above the mobile locations, with the branch count. */
export const ContactMobileViewToggle = ({
    view,
    onChange,
    locationCount,
}: ContactMobileViewToggleProps) => (
    <div className="flex items-center justify-between md:hidden">
        <div
            role="tablist"
            aria-label="Show locations as"
            className="flex rounded-full bg-white p-1"
        >
            {VIEWS.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={view === option.value}
                    onClick={() => onChange(option.value)}
                    className={cn(
                        'h-9 rounded-full px-5 font-sans text-sm transition-colors',
                        view === option.value
                            ? 'bg-[#1f1f21] text-white'
                            : 'text-[#1f1f21]',
                    )}
                >
                    {option.label}
                </button>
            ))}
        </div>
        <p className="m-0 font-sans text-sm text-[#1f1f21]/60">
            {locationCount === 1 ? '1 location' : `${locationCount} locations`}
        </p>
    </div>
);

interface ContactMobileLocationListProps {
    locations: LocationDataType[];
    professionalId: number;
    professionalTitle: string;
    onViewOnMap: (location: LocationDataType) => void;
}

/**
 * Every branch as a card, the mobile counterpart of the desktop map overlay,
 * paged like the legacy mobile list so a large network doesn't push the
 * enquiry form out of reach.
 */
export const ContactMobileLocationList = ({
    locations,
    professionalId,
    professionalTitle,
    onViewOnMap,
}: ContactMobileLocationListProps) => {
    const [visibleCount, setVisibleCount] = useState(
        MOBILE_INITIAL_LOCATION_NUM,
    );
    const headOfficeId = locations[0]?.ID;

    return (
        <div className="flex flex-col gap-4 md:hidden">
            {locations.slice(0, visibleCount).map((location) => (
                <ProfessionalLocationCard
                    key={location.ID}
                    location={location}
                    professionalId={professionalId}
                    title={location.Title || professionalTitle}
                    isHeadOffice={location.ID === headOfficeId}
                    onViewOnMap={
                        hasMapCoordinates(location)
                            ? () => onViewOnMap(location)
                            : undefined
                    }
                />
            ))}
            {locations.length > visibleCount ? (
                <button
                    type="button"
                    onClick={() =>
                        setVisibleCount(
                            (count) => count + MOBILE_INITIAL_LOCATION_NUM,
                        )
                    }
                    className="inline-flex h-[2.625rem] items-center justify-center rounded-full border border-black/10 bg-white px-5 font-sans text-base text-[#333333] transition-colors hover:bg-black/5"
                >
                    Load more
                </button>
            ) : null}
        </div>
    );
};
